/**
 * Which products belong on a staff-facing ordering grid (F-FE-N161).
 *
 * `is_available` carries two different meanings and the till has to tell them
 * apart:
 *
 *   "86'd — we are out of this today"
 *       is_available = false, is_storefront_visible = true
 *       The tile STAYS, greyed out. The cashier needs to see that the product
 *       exists so they can tell the customer, and so nobody re-creates it as a
 *       duplicate. This is the behaviour the counter has always had.
 *
 *   "retired — we do not sell this any more"
 *       is_available = false, is_storefront_visible = false
 *       Unorderable at the counter AND absent from the storefront: there is no
 *       surface left on which it can be sold. The tile is dead weight, and on an
 *       older tenant there can be a lot of it — Parched carried eight.
 *
 * So retirement is not a new flag; it is already expressed by the two existing
 * ones being false together. That is deliberately the narrowest possible rule:
 * it can only ever hide a product that is already unorderable everywhere, so it
 * cannot take a sellable item off the till.
 *
 * Deleting was never the alternative. `order_items.product_id` is
 * ON DELETE CASCADE, so removing a retired product takes its sales history with
 * it — and these products do have history (one of Parched's has 204 order
 * lines). Hiding keeps every report honest.
 *
 * Reports, Z-slips and tax reconciliation must NEVER use this. They report what
 * was sold, not what is on offer.
 */

/** The fields this decision needs; anything Product-shaped satisfies it. */
export interface ProductVisibilityFields {
  is_available: boolean
  is_storefront_visible?: boolean
}

/**
 * True when a product is unorderable on every surface and should not occupy a
 * tile on a staff ordering grid.
 *
 * `is_storefront_visible` is optional on the wire, and an absent value is read
 * as visible — the same default the column has. That makes the rule fail-open:
 * a payload from an older backend that omits the field can never cause a
 * product to disappear from the till.
 */
export function isRetiredProduct(p: ProductVisibilityFields): boolean {
  return p.is_available === false && p.is_storefront_visible === false
}

/** Drop retired products from a staff ordering grid, preserving order. */
export function withoutRetiredProducts<T extends ProductVisibilityFields>(
  products: T[],
): T[] {
  return products.filter((p) => !isRetiredProduct(p))
}
